'use client';

import React, { useState, useEffect } from 'react';

interface NavbarProps {
  activePage: string;
  onNavigate: (page: string) => void;
}

const Navbar: React.FC<NavbarProps> = ({ activePage, onNavigate }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'tutors', label: 'Our Tutors' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'shop', label: 'Shop' },
    { id: 'newsletter', label: 'Newsletter' },
    { id: 'contact', label: 'Contact' },
  ];

  const handleNav = (page: string) => {
    onNavigate(page);
    setMenuOpen(false);
  };

  return (
    <nav
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: 'rgba(10,22,40,0.97)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(244,196,48,0.2)',
        transition: 'all 0.3s',
      }}
    >
      {/* Main bar */}
      <div
        className="navbar-inner"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.75rem 1.25rem',
          maxWidth: 1200,
          margin: '0 auto',
          minHeight: 64,
        }}
      >
        {/* Logo */}
        <div
          onClick={() => handleNav('home')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.6rem',
            cursor: 'pointer',
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.2rem',
            fontWeight: 700,
            color: '#f4c430',
            letterSpacing: '0.02em',
            flexShrink: 0,
          }}
        >
          <div
            className="navbar-logo-circle"
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            <img
              src="/assets/images/owl_only-1779411032040.png"
              alt="StellarScholar owl logo"
              style={{ width: '85%', height: '85%', objectFit: 'contain' }}
            />
          </div>
          <span className="navbar-brand-text">Stellar<span style={{ color: '#ffffff' }}>Scholar</span></span>
        </div>

        {/* Desktop Nav Links */}
        <ul
          className="desktop-nav"
          style={{ display: 'flex', gap: '0.5rem', listStyle: 'none', margin: 0, padding: 0 }}
        >
          {navLinks.map((link) => (
            <li key={link.id}>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleNav(link.id); }}
                style={{
                  color: activePage === link.id ? '#f4c430' : 'rgba(255,255,255,0.7)',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  padding: '0.5rem 1rem',
                  borderRadius: 6,
                  background: activePage === link.id ? 'rgba(244,196,48,0.1)' : 'transparent',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {link.label}
              </a>
            </li>
          ))}
          <li>
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); handleNav('signup'); }}
              style={{
                background: '#f4c430',
                color: '#0a1628',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontWeight: 600,
                padding: '0.5rem 1rem',
                borderRadius: 8,
                transition: 'all 0.2s',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Sign Up
            </a>
          </li>
        </ul>

        {/* Hamburger Button (mobile only) */}
        <button
          className="hamburger-btn"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            flexShrink: 0,
          }}
        >
          <span
            style={{
              display: 'block',
              width: 26,
              height: 2,
              background: '#f4c430',
              borderRadius: 2,
              transition: 'all 0.3s',
              transform: menuOpen ? 'translateY(7px) rotate(45deg)' : 'none',
            }}
          />
          <span
            style={{
              display: 'block',
              width: 26,
              height: 2,
              background: '#f4c430',
              borderRadius: 2,
              transition: 'all 0.3s',
              opacity: menuOpen ? 0 : 1,
            }}
          />
          <span
            style={{
              display: 'block',
              width: 26,
              height: 2,
              background: '#f4c430',
              borderRadius: 2,
              transition: 'all 0.3s',
              transform: menuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none',
            }}
          />
        </button>
      </div>

      {/* Mobile Dropdown Menu */}
      {menuOpen && (
        <div
          className="mobile-menu"
          style={{
            background: 'rgba(10,22,40,0.99)',
            borderTop: '1px solid rgba(244,196,48,0.15)',
            padding: '0.75rem 1.25rem 1.25rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
          }}
        >
          {navLinks.map((link) => (
            <a
              key={link.id}
              href="#"
              onClick={(e) => { e.preventDefault(); handleNav(link.id); }}
              style={{
                color: activePage === link.id ? '#f4c430' : 'rgba(255,255,255,0.85)',
                textDecoration: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                padding: '0.85rem 0.75rem',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                background: activePage === link.id ? 'rgba(244,196,48,0.08)' : 'transparent',
                borderRadius: 6,
                cursor: 'pointer',
                display: 'block',
              }}
            >
              {link.label}
            </a>
          ))}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); handleNav('signup'); }}
            style={{
              background: '#f4c430',
              color: '#0a1628',
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: 700,
              padding: '0.85rem 1rem',
              borderRadius: 8,
              textAlign: 'center',
              marginTop: '0.75rem',
              cursor: 'pointer',
              display: 'block',
            }}
          >
            Sign Up
          </a>
        </div>
      )}

      <style>{`
        .desktop-nav { display: flex !important; }
        .hamburger-btn { display: none !important; }
        .mobile-menu { display: none !important; }

        @media (max-width: 640px) {
          .desktop-nav { display: none !important; }
          .hamburger-btn { display: flex !important; }
          .mobile-menu { display: flex !important; }
          .navbar-logo-circle {
            width: 40px !important;
            height: 40px !important;
          }
          .navbar-brand-text {
            font-size: 1.05rem !important;
          }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
